import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { updatePageSEO } from '../utils/seo';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CommonNumbers = () => {
  const [commonNumbersData, setCommonNumbersData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Update SEO for Common Numbers page
    updatePageSEO({
      path: '/common-numbers',
      title: 'Morning Sunday Teer Common Number Today | Direct House Ending',
      description: 'Get today\'s Morning Sunday Teer Common Number with Direct, House and Ending predictions. Updated daily for accurate Teer number forecasting.',
      robots: 'index, follow'
    });
    
    fetchCommonNumbers();
  }, []);

  const fetchCommonNumbers = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/common-number-data/current`);
      const data = response.data;
      
      // Transform API data to display format
      setCommonNumbersData({
        date: data.date,
        predictions: [
          { 
            direct: data.row1Direct, 
            house: data.row1House, 
            ending: data.row1Ending 
          },
          { 
            direct: data.row2Direct, 
            house: data.row2House, 
            ending: data.row2Ending 
          }
        ]
      });
    } catch (error) {
      console.error('Error fetching common numbers:', error);
      // Keep default values if API fails
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Banner */}
      <div className="relative h-64 sm:h-80 bg-gradient-to-br from-blue-900 via-purple-800 to-blue-700 overflow-hidden">
        <div className="absolute inset-0 bg-black/30"></div>
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4">
          <div className="text-5xl sm:text-6xl md:text-7xl font-black text-yellow-400 mb-2 opacity-20 uppercase tracking-widest">
            Morning
          </div>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-black text-white mb-2 drop-shadow-lg relative z-10">
            Sunday Morning Teer
          </h1>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-black text-yellow-400 mb-4 drop-shadow-lg relative z-10">
            Common Number
          </h2>
          <p className="text-lg sm:text-xl text-white/90 font-medium relative z-10">
            Check today's predictive target numbers
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Common Numbers Table */}
        <div className="bg-white rounded-2xl shadow-2xl overflow-hidden border-4 border-coral-600 mb-8">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-20">
              <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-coral-600 mb-4"></div>
              <p className="text-gray-600 text-lg">Loading common numbers...</p>
            </div>
          ) : commonNumbersData ? (
            <>
              {/* Date Header */}
              <div className="bg-coral-600 text-white text-center py-5">
                <h3 className="text-2xl sm:text-3xl font-bold uppercase tracking-wide">
                  DATE: {commonNumbersData.date}
                </h3>
              </div>

              {/* Table */}
              <div className="overflow-x-auto">
                <table className="w-full">
                  {/* First Row Header */}
                  <thead>
                    <tr className="bg-gray-900 text-white">
                      <th className="py-4 px-6 text-xl sm:text-2xl font-bold border-r border-gray-700">Direct</th>
                      <th className="py-4 px-6 text-xl sm:text-2xl font-bold border-r border-gray-700">House</th>
                      <th className="py-4 px-6 text-xl sm:text-2xl font-bold">Ending</th>
                    </tr>
                  </thead>
                  <tbody>
                    {/* First Row Data */}
                    <tr className="bg-white">
                      <td className="py-6 px-6 text-center text-4xl sm:text-5xl font-black text-gray-900 border-r border-gray-200">
                        {commonNumbersData.predictions[0].direct}
                      </td>
                      <td className="py-6 px-6 text-center text-4xl sm:text-5xl font-black text-gray-900 border-r border-gray-200">
                        {commonNumbersData.predictions[0].house}
                      </td>
                      <td className="py-6 px-6 text-center text-4xl sm:text-5xl font-black text-gray-900">
                        {commonNumbersData.predictions[0].ending}
                      </td>
                    </tr>
                    
                    {/* Second Row Header */}
                    <tr className="bg-gray-900 text-white">
                      <th className="py-4 px-6 text-xl sm:text-2xl font-bold border-r border-gray-700">Direct</th>
                      <th className="py-4 px-6 text-xl sm:text-2xl font-bold border-r border-gray-700">House</th>
                      <th className="py-4 px-6 text-xl sm:text-2xl font-bold">Ending</th>
                    </tr>
                    
                    {/* Second Row Data */}
                    <tr className="bg-white">
                      <td className="py-6 px-6 text-center text-4xl sm:text-5xl font-black text-gray-900 border-r border-gray-200">
                        {commonNumbersData.predictions[1].direct}
                      </td>
                      <td className="py-6 px-6 text-center text-4xl sm:text-5xl font-black text-gray-900 border-r border-gray-200">
                        {commonNumbersData.predictions[1].house}
                      </td>
                      <td className="py-6 px-6 text-center text-4xl sm:text-5xl font-black text-gray-900">
                        {commonNumbersData.predictions[1].ending}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </>
          ) : (
            <div className="py-20 text-center text-gray-600">
              <p className="text-lg">No data available</p>
            </div>
          )}
        </div>

        {/* Disclaimer */}
        <div className="bg-yellow-50 border-4 border-yellow-400 rounded-2xl p-6 sm:p-8 mb-8">
          <h3 className="text-2xl font-bold text-yellow-800 text-center mb-4">Disclaimer</h3>
          <p className="text-gray-700 text-center leading-relaxed text-base sm:text-lg">
            These common numbers are purely based on certain calculations done using past results. There is no guarantee of the accuracy of these numbers. Please play responsibly.
          </p>
        </div>

        {/* Content Section */}
        <div className="prose prose-lg max-w-none">
          <p className="text-gray-700 leading-relaxed mb-6">
            If you enjoy following Teer games, you have probably come across the term <strong>Sunday Morning Teer Common Number</strong>. It is one of the most searched topics among people who like studying daily number trends before the official results are announced.
          </p>
          
          <p className="text-gray-700 leading-relaxed mb-6">
            It is important to understand that a <strong>Sunday Morning Teer Common Number</strong> is not an official result. Instead, it is a prediction created by studying previous outcomes, historical records, recurring trends, and community discussions. Think of it as a roadmap rather than a destination. It may help guide your analysis, but it cannot promise any specific outcome.
          </p>

          <h2 className="text-3xl font-bold text-gray-900 mb-4 border-b-2 border-gray-300 pb-2">
            What Is Sunday Morning Teer Common Number?
          </h2>
          
          <p className="text-gray-700 leading-relaxed mb-6">
            The <strong>Sunday Morning Teer Common Number</strong> is a collection of numbers that many prediction followers believe have a stronger chance of appearing on a particular Sunday morning. These numbers are shared before the official Teer result is declared.
          </p>

          <p className="text-gray-700 leading-relaxed mb-6">
            Unlike official winning numbers, common numbers are based on <strong>observation rather than certainty</strong>. Analysts review previous results, compare historical data, and study recent number movements to prepare their daily forecasts.
          </p>
        </div>
      </div>
    </div>
  );
};

export default CommonNumbers;
